import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { LoadingPopup } from 'components/common';
import { Inputs, Select } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { OptionItem } from 'models';
import {
  BODY_CREATE_PACKAGE,
  BODY_UPDATE_PACKAGE,
  PACKAGE_STATUSES,
  PACKAGE_TYPE,
} from 'models/package';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createPackageApi, editPackageApi } from 'services/package';

interface Props {
  open: boolean;
  subject?: PACKAGE_TYPE;
  onSuccess: () => void;
  handleClosePopup: () => void;
}

interface IFormInputs {
  name: string;
  description: string;
  tag: string;
  price: number;
  status?: OptionItem;
  parent_rate: number;
  sitter_rate: number;
}

const defaultValues: Partial<IFormInputs> = {
  name: '',
  description: '',
  tag: '',
  parent_rate: undefined,
  sitter_rate: undefined,
  price: undefined,
  status: undefined,
};

const PopupCreateEditPackage = (props: Props) => {
  const { open, handleClosePopup, onSuccess, subject } = props;

  const [loadingPopup, setLoadingPopup] = useState(false);

  const theme = useTheme();

  const schema = useMemo(() => {
    return yup.object({
      name: yup.string().required('Name is required'),
      description: yup.string().required('Description is required'),
      tag: yup.string().notRequired(),
      parent_rate: yup
        .number()
        .typeError('Parent rate is required')
        .min(0, 'Parent rate must be greater than or equal to 0')
        .required('Parent rate is required'),
      sitter_rate: yup
        .number()
        .typeError('Sitter rate is required')
        .min(0, 'Sitter rate must be greater than or equal to 0')
        .required('Sitter rate is required'),
      price: yup
        .number()
        .typeError('Price is required')
        .min(0, 'Price must be greater than or equal to 0')
        .required('Price is required'),
      status: subject
        ? yup.mixed().required('Status is required')
        : yup.mixed().notRequired(),
    });
  }, [subject]);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const { reset } = form;

  const _handleClosePopup = () => {
    handleClosePopup();
    reset({ ...defaultValues });
  };

  useEffect(() => {
    if (!open) return;
    if (subject) {
      reset({
        name: subject.name,
        description: subject.description,
        tag: subject.tag,
        price: subject.price,
        status: PACKAGE_STATUSES.find((item) => item.id === subject.status),
        parent_rate: subject.parent_rate,
        sitter_rate: subject.sitter_rate,
      });
    }
  }, [open, subject, reset]);

  const onSubmit = async (value: IFormInputs) => {
    const _value: BODY_CREATE_PACKAGE | BODY_UPDATE_PACKAGE = subject
      ? {
          ...value,
          status: value.status?.id,
        }
      : value;
    setLoadingPopup(true);
    await (subject
      ? editPackageApi(subject.id, _value as BODY_UPDATE_PACKAGE)
      : createPackageApi(_value as BODY_CREATE_PACKAGE)
    )
      .then((r) => {
        onSuccess();
        _handleClosePopup();
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setLoadingPopup(false);
      });
  };
  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 527,
          borderRadius: 0,
        },
      }}
    >
      <Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            {subject ? 'Edit' : 'Create'} Package
          </Typography>
          <IconButton
            onClick={_handleClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
        <Divider />
        {loadingPopup && <LoadingPopup color="primary" />}

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack spacing="20px" p="25px 24px">
            <Stack>
              <Inputs
                title="Package Name"
                form={form}
                name="name"
                placeholder="Enter package name"
              />
            </Stack>
            <Stack>
              <Inputs
                title="Description"
                form={form}
                name="description"
                placeholder="Enter description"
              />
            </Stack>
            <Stack>
              <Inputs
                type="number"
                title="Price"
                form={form}
                name="price"
                placeholder="Enter price"
                disabled={subject ? true : false}
              />
            </Stack>
            <Stack>
              <Inputs
                type="number"
                title="Parent Rate"
                form={form}
                name="parent_rate"
                placeholder="Enter parent rate"
                disabled={subject ? true : false}
              />
            </Stack>
            <Stack>
              <Inputs
                type="number"
                title="Sitter Rate"
                form={form}
                name="sitter_rate"
                placeholder="Enter sitter rate"
                disabled={subject ? true : false}
              />
            </Stack>
            <Stack>
              <Inputs
                title="Tag Value"
                form={form}
                name="tag"
                placeholder="Enter tag value"
              />
            </Stack>
            <Divider />
            {subject && (
              <Stack>
                <Select
                  title="Status"
                  name="status"
                  bindLabel="name"
                  form={form}
                  filterFunc={true}
                  options={PACKAGE_STATUSES}
                />
              </Stack>
            )}
            <Stack direction="row" spacing="8px">
              <Button
                variant="outlined"
                color="secondary"
                onClick={_handleClosePopup}
                sx={{
                  height: 30,
                  width: '100%',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.1,
                  border: `1px solid ${theme.palette.secondary.main}`,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{
                  height: 30,
                  width: '100%',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.1,
                }}
              >
                {subject ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Grid>
    </Dialog>
  );
};

export default PopupCreateEditPackage;
